import { SodiumPlus, X25519PublicKey, X25519SecretKey } from 'sodium-plus';
import { Buffer } from 'buffer';

import { IPairKey } from '@/store/models/encryption.model';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';

export class AsymmetricTreezorEncryption {
  private static instance: AsymmetricTreezorEncryption;
  private sodium: SodiumPlus | undefined;

  private constructor() {}

  static getInstance(): AsymmetricTreezorEncryption {
    if (!AsymmetricTreezorEncryption.instance) {
      this.instance = new AsymmetricTreezorEncryption();
    }

    return this.instance;
  }

  async init() {
    if (!this.sodium) {
      this.sodium = await SodiumPlus.auto();
    }
  }

  private convertHexToString(hex: string) {
    let str = '';
    for (let i = 0; i < hex.length; i += 2) {
      const v = parseInt(hex.substr(i, 2), 16);
      if (v) str += String.fromCharCode(v);
    }
    return str;
  }

  public adaptPrivateKey(publicKey: string, privateKey: string): string {
    const { api } = getStaticDashboardConfig();

    if (publicKey.length < privateKey.length && api.encryption === 'hexa') {
      return this.convertHexToString(privateKey);
    }

    return privateKey;
  }

  async generateKeyPair(): Promise<IPairKey> {
    try {
      if (!this.sodium) return Promise.reject('[AsymmetricTreezorEncryption] Sodium not initialized');

      const keyPair = await this.sodium?.crypto_box_keypair();

      const publicKeyBin = await this.sodium?.crypto_box_publickey(keyPair);
      const privateKeyBin = await this.sodium?.crypto_box_secretkey(keyPair);

      const publicKeyHex = await this.sodium?.sodium_bin2hex(publicKeyBin.getBuffer());
      const privateKeyHex = await this.sodium?.sodium_bin2hex(privateKeyBin.getBuffer());

      return {
        publicKey: publicKeyHex,
        privateKey: privateKeyHex,
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async encryptKey(key: string, publicKey: string): Promise<string> {
    try {
      if (!this.sodium) return Promise.reject('[AsymmetricTreezorEncryption] Sodium not initialized');

      const publicKeyBin = await this.sodium.sodium_hex2bin(publicKey);

      const encryptedKeyBin = await this.sodium.crypto_box_seal(key, new X25519PublicKey(publicKeyBin));

      return await this.sodium.sodium_bin2hex(encryptedKeyBin);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async decryptKey(encryptedKey: string, publicKey: string, privateKey: string): Promise<string> {
    try {
      if (!this.sodium) return Promise.reject('[AsymmetricTreezorEncryption] Sodium not initialized');

      const { api } = getStaticDashboardConfig();

      privateKey = this.adaptPrivateKey(publicKey, privateKey);

      const encrypptedKeyBin = await this.sodium.sodium_hex2bin(encryptedKey);
      const publicKeyBin = await this.sodium.sodium_hex2bin(publicKey);
      const privateKeyBin = await this.sodium.sodium_hex2bin(privateKey);

      const decryptedKeyBin = await this.sodium.crypto_box_seal_open(
        encrypptedKeyBin,
        new X25519PublicKey(publicKeyBin),
        new X25519SecretKey(privateKeyBin),
      );

      if (api.encryption === 'default') return decryptedKeyBin.toString();

      return await this.sodium.sodium_bin2hex(decryptedKeyBin);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async decryptData(encryptedData: string, publicKey: string, privateKey: string): Promise<string> {
    try {
      if (!this.sodium) return Promise.reject('[AsymmetricTreezorEncryption] Sodium not initialized');

      const publicKeyBin = await this.sodium.sodium_hex2bin(publicKey);
      const privateKeyBin = await this.sodium.sodium_hex2bin(privateKey);

      const decryptedDataBin = await this.sodium.crypto_box_seal_open(
        Buffer.from(encryptedData, 'base64'),
        new X25519PublicKey(publicKeyBin),
        new X25519SecretKey(privateKeyBin),
      );

      return decryptedDataBin.toString();
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
